import { type Locale, setDefaultOptions } from 'date-fns'
import { enUS, es, frCA, pl, uk } from 'date-fns/locale'
import type { i18n as I18n } from 'i18next'

// date-fns has its own locale objects, independent of the i18next language
// catalog. This map bridges the two: for each app language we pick the date-fns
// locale that drives localized format tokens (P / PP / p — see src/utils/date),
// so date/time ordering and separators follow the user's locale (e.g. en-US →
// 02/20/2026, fr-CA → 2026-02-20). Keyed by the full SUPPORTED_LANGUAGES tag,
// not just base language - fr-CA and es-US are genuinely divergent variants
// (Canadian French, US Spanish), so they need their own entries rather than
// collapsing to a generic "fr"/"es" bucket; date-fns has no dedicated es-US
// locale, so es-US uses base `es` as the closest available. Keep in sync as
// locales are added.
const DATE_FNS_LOCALES: Record<string, Locale> = {
  'en-US': enUS,
  'es-US': es,
  'fr-CA': frCA,
  pl,
  uk,
}

// Resolve on the full tag first (e.g. "fr-CA" → frCA), falling back to base
// language ("fr-CA" → "fr") for anything only mapped generically, then en-US.
// This is independent of i18next's own `load` setting (src/i18n/index.ts uses
// `currentOnly`); it's just how this map is keyed.
const resolveLocale = (language: string): Locale =>
  DATE_FNS_LOCALES[language] ?? DATE_FNS_LOCALES[language.split('-')[0]] ?? enUS

// Wire the active i18next language into date-fns' global default locale, so the
// pure format helpers in src/utils/date stay locale-aware without every call
// site threading a locale through. Runs once on init and again on every
// language change; React re-renders via useTranslation pick up the new output.
export const syncDateFnsLocale = (i18n: I18n): void => {
  setDefaultOptions({ locale: resolveLocale(i18n.language) })
  i18n.on('languageChanged', (language) => {
    setDefaultOptions({ locale: resolveLocale(language) })
  })
}
